import { Ability, AbilityBuilder, type ForcedSubject, subject } from '@casl/ability';
import type { LooseObject } from '@piccolohealth/util';
import type { Role, User } from '../graphql/types';
export { subject };

export const SUBJECTS = [
  'all',
  'auditTrail',
  'authedRecording',
  'behaviour',
  'form',
  'formLabel',
  'incident',
  'incidentCategory',
  'incidentFile',
  'incidentLabel',
  'label',
  'location',
  'organization',
  'participant',
  'participantBehaviour',
  'participantBehaviourRecordingMethod',
  'participantBehaviourRecording',
  'participantDocument',
  'participantDocumentComment',
  'participantDocumentLabel',
  'participantEmbeddableForm',
  'participantFieldGroupTemplate',
  'participantFieldTemplate',
  'participantField',
  'participantFile',
  'participantFileLabel',
  'participantNote',
  'participantNoteLabel',
  'participantLabel',
  'participantQr',
  'participantQrLabel',
  'participantRestrictivePractice',
  'participantRestrictivePracticeLabel',
  'participantTask',
  'participantTaskLabel',
  'participantTaskTemplate',
  'participantTaskTemplateLabel',
  'participantUser',
  'participantUserLabel',
  'template',
  'templateScoringChecklist',
  'templateLabel',
  'user',
] as const;

export const ACTIONS = [
  'manage',
  'create',
  'read',
  'update',
  'delete',
  'list',
  'review',
  'fastForward',
] as const;

export type Roles = `${Role}`;
export type Actions = (typeof ACTIONS)[number];
export type Subjects = (typeof SUBJECTS)[number];

export type Subject = ReturnType<typeof subject<Exclude<Subjects, 'all'>, LooseObject>>;

export type UserInfo = {
  user: User;
  roles: Roles[];
  organizationId?: string;
  participants?: string[];
  incidents?: {
    reviewer: string[];
    reporter: string[];
    all: string[];
  };
};

export type AppAbilities = [Actions, Subjects | ForcedSubject<Exclude<Subjects, 'all'>>];
export class AppAbility extends Ability<AppAbilities, any> {}

type DefinePermissions = (userInfo: UserInfo, builder: AbilityBuilder<AppAbility>) => void;

const rolePermissions: Record<Roles, DefinePermissions> = {
  Support: (_userInfo, { can }) => {
    // Support can do anything
    can('manage', 'all');
  },

  Administrator: (userInfo, { can, cannot }) => {
    // Can do anything within their org
    can('manage', 'all', { organizationId: userInfo.organizationId });

    // Can update their own user
    can('update', 'user', { userId: userInfo.user.id });

    // Cannot create/delete organizations
    cannot(['create', 'delete'], 'organization');
  },

  TeamLeader: (userInfo, { can }) => {
    can(['read', 'list'], 'organization', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'behaviour', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'form', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'formLabel', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'label', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'location', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'template', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'templateScoringChecklist', { organizationId: userInfo.organizationId });

    can(['read', 'list'], 'user', { organizationId: userInfo.organizationId });
    can(['update'], 'user', { userId: userInfo.user.id });

    can(['list'], 'participant', { organizationId: userInfo.organizationId });
    can('manage', 'participantUser', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['read', 'update', 'fastForward'], ['participant'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['read', 'list'], 'participantFieldGroupTemplate', {
      organizationId: userInfo.organizationId,
    });
    can(['read', 'list'], 'participantFieldTemplate', { organizationId: userInfo.organizationId });
    can(['manage'], 'participantDocument', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantDocumentComment', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantDocumentLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantFile', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantFileLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantNote', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });

    can(['manage'], 'participantUserLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantNoteLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantQr'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantEmbeddableForm'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantQrLabel'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantRestrictivePractice'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantRestrictivePracticeLabel'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantTask', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantTaskLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['read', 'list'], 'participantTaskTemplate', {
      organizationId: userInfo.organizationId,
    });
    can(['read', 'list'], 'participantTaskTemplateLabel', {
      organizationId: userInfo.organizationId,
    });
    can(['manage'], 'participantBehaviour', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantBehaviourRecording', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantBehaviourRecordingMethod', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantLabel'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });

    can('list', 'incident', { organizationId: userInfo.organizationId });
    can('create', 'incident', {
      organizationId: userInfo.organizationId ?? [],
    });
    can(['read', 'update'], 'incident', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.all ?? [],
      },
    });
    can('review', 'incident', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.reviewer ?? [],
      },
    });
    can('manage', 'incidentFile', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.all ?? [],
      },
    });
    can(['manage'], 'incidentLabel', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.all ?? [],
      },
    });
    can(['read', 'list'], 'incidentCategory', {
      organizationId: userInfo.organizationId,
    });
  },

  TeamMember: (userInfo, { can }) => {
    can(['read', 'list'], 'organization', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'behaviour', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'form', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'formLabel', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'label', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'location', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'template', { organizationId: userInfo.organizationId });
    can(['read', 'list'], 'templateScoringChecklist', { organizationId: userInfo.organizationId });

    can(['read', 'list'], 'user', { organizationId: userInfo.organizationId });
    can(['update'], 'user', { userId: userInfo.user.id });

    can(['list'], 'participant', { organizationId: userInfo.organizationId });
    can(['read', 'update'], ['participant'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['read', 'list'], 'participantFieldGroupTemplate', {
      organizationId: userInfo.organizationId,
    });
    can(['read', 'list'], 'participantFieldTemplate', { organizationId: userInfo.organizationId });
    can(['manage'], 'participantDocument', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantDocumentLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantFile', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantFileLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantNote', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantNoteLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantLabel'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantQr'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantEmbeddableForm'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantQrLabel'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantRestrictivePractice'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], ['participantRestrictivePracticeLabel'], {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantTask', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantTaskLabel', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['read', 'list'], 'participantTaskTemplate', {
      organizationId: userInfo.organizationId,
    });
    can(['read', 'list'], 'participantTaskTemplateLabel', {
      organizationId: userInfo.organizationId,
    });
    can(['manage'], 'participantBehaviour', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantBehaviourRecording', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can(['manage'], 'participantBehaviourRecordingMethod', {
      organizationId: userInfo.organizationId,
      participantId: {
        $in: userInfo.participants ?? [],
      },
    });
    can('list', 'incident', { organizationId: userInfo.organizationId });
    can('create', 'incident', {
      organizationId: userInfo.organizationId,
    });
    can(['read', 'update'], 'incident', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.all ?? [],
      },
    });
    can('manage', 'incidentFile', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.all ?? [],
      },
    });
    can(['manage'], 'incidentLabel', {
      organizationId: userInfo.organizationId,
      incidentId: {
        $in: userInfo.incidents?.all ?? [],
      },
    });
    can(['read', 'list'], 'incidentCategory', {
      organizationId: userInfo.organizationId,
    });
  },
};

export const defineAbilityFor = (userInfo: UserInfo): AppAbility => {
  const builder = new AbilityBuilder<AppAbility>(AppAbility);

  userInfo.roles.forEach((role) => {
    const permissionSet = rolePermissions[role];

    if (permissionSet) {
      permissionSet(userInfo, builder);
    }
  });

  // All users get the authedRecording permission.
  // It exists outside of the role-based permissions
  builder.can('read', 'authedRecording');

  // All users can read their own user
  builder.can('read', 'user', { userId: userInfo.user.id });

  // All users can list organizations
  builder.can('list', 'organization');

  return builder.build();
};
